import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { BookOpen, Clock, User, ChevronLeft, ChevronRight, Send, Search, Sparkles } from 'lucide-react';
import { DataContext } from '../context/DataProvider';
import { defaultBlogs } from '../data/defaultBlogs';

const defaultCategories = ['All', 'Development', 'AI Tools', 'SEO', 'Marketing', 'UI/UX'];

const Blog = () => {
    const { blogs, loading } = useContext(DataContext);
    const [activeCategory, setActiveCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 6;

    // Use DB posts if available, fallback to default SEO keyword-rich articles
    const allPosts = blogs && blogs.length > 0 ? blogs : defaultBlogs;
    const publishedPosts = allPosts.filter(post => post.is_published !== false);

    // Filter by category and search
    const filteredPosts = publishedPosts.filter((post) => {
        const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
        const matchesSearch = searchQuery === '' || 
            (post.title && post.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (post.category && post.category.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredPosts.length / postsPerPage) || 1;
    const startIndex = (currentPage - 1) * postsPerPage;
    const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email) {
            setSubscribed(true);
            setEmail('');
            setTimeout(() => setSubscribed(false), 5000);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Extract categories dynamically from posts
    const dynamicCategories = Array.from(new Set(['All', ...defaultCategories, ...publishedPosts.map(p => p.category).filter(Boolean)]));

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": "https://pyrusmedia.in/blog#blog",
        "url": "https://pyrusmedia.in/blog",
        "name": "PyrusMedia Blog",
        "description": "Read PyrusMedia insights on digital marketing, SEO, web development, mobile applications, AI automation, and technology.",
        "isPartOf": {
            "@id": "https://pyrusmedia.in/#website"
        },
        "publisher": {
            "@type": "Organization",
            "name": "PyrusMedia",
            "url": "https://pyrusmedia.in/"
        }
    };

    return (
        <>
            <Helmet>
                <title>Blog & Tech Insights | Custom Web Apps, AI & SEO Guides | PyrusMedia</title>
                <meta name="description" content="Explore expert guides on custom web application development, AI automation for small businesses, local SEO strategies in Pune, and Generative Engine Optimization (GEO)." />
                <meta name="keywords" content="benefits of custom web applications, how to choose a mobile app developer, AI automation benefits for small businesses, SEO benefits for small businesses, digital marketing strategy for startups, custom software vs off the shelf software, how to rank in AI search results" />
                <link rel="canonical" href="https://pyrusmedia.in/blog" />
                <script type="application/ld+json">
                    {JSON.stringify(schemaData)}
                </script>
            </Helmet>

            {/* Hero Section */}
            <section className="pt-32 pb-16 bg-white text-center relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#bebd19]/20 rounded-full blur-[100px] pointer-events-none" />
                <div className="container mx-auto px-4 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-semibold text-gray-700 mb-6">
                        <Sparkles size={14} className="text-[#bebd19]" />
                        <span>PyrusMedia Knowledge Hub</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight">Insights & Articles</h1>
                    <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-8">
                        Discover expert tutorials, web app guides, UI/UX design tips, and AI updates.
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-md mx-auto relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder="Search articles by title or keyword..."
                            className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#bebd19] focus:bg-white transition-all shadow-sm"
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>
                </div>
            </section>

            {/* Category Filters */}
            <Section className="bg-white pt-2 pb-6">
                <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
                    {dynamicCategories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => {
                                setActiveCategory(cat);
                                setCurrentPage(1);
                            }}
                            className={`px-5 py-2 rounded-full text-xs font-bold transition-all duration-300 border ${
                                activeCategory === cat
                                    ? 'bg-[#bebd19] text-gray-900 border-[#bebd19] shadow-sm'
                                    : 'bg-gray-100 text-gray-600 border-gray-200 hover:border-[#bebd19] hover:text-gray-900'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </Section>

            {/* Blog Grid Section */}
            <Section className="bg-gray-50 py-16">
                <div className="container mx-auto px-4">
                    {loading ? (
                        <div className="text-center py-20">
                            <div className="w-12 h-12 border-4 border-[#bebd19] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-500 font-medium">Fetching articles...</p>
                        </div>
                    ) : currentPosts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {currentPosts.map((post, index) => (
                                <motion.article
                                    key={post.id || index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#bebd19] hover:shadow-lg transition-all duration-300 flex flex-col"
                                >
                                    {/* Cover Image or Thumbnail */}
                                    <Link to={`/blog/${post.slug}`} className="block h-48 bg-gray-100 overflow-hidden relative">
                                        {post.cover_image ? (
                                            <img
                                                src={post.cover_image}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200">
                                                <BookOpen className="w-12 h-12 text-gray-300 group-hover:text-[#bebd19] transition-colors" />
                                            </div>
                                        )}
                                        {post.category && (
                                            <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-xs font-bold text-gray-900 border border-gray-200">
                                                {post.category}
                                            </span>
                                        )}
                                    </Link>

                                    <div className="p-6 flex flex-col flex-grow">
                                        <Link to={`/blog/${post.slug}`} className="block group-hover:text-[#bebd19] transition-colors">
                                            <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-snug">
                                                {post.title}
                                            </h3>
                                        </Link>

                                        {post.excerpt && (
                                            <p className="text-gray-500 text-sm mb-6 line-clamp-3 flex-grow leading-relaxed">
                                                {post.excerpt}
                                            </p>
                                        )}

                                        <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100 mt-auto">
                                            <span className="flex items-center gap-1.5 font-medium text-gray-700">
                                                <User size={14} className="text-[#bebd19]" /> {post.author || 'PyrusMedia'}
                                            </span>
                                            <div className="flex items-center gap-3">
                                                {post.created_at && <span>{formatDate(post.created_at)}</span>}
                                                {post.read_time && (
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={12} /> {post.read_time}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-white rounded-2xl border border-gray-200 max-w-2xl mx-auto shadow-sm">
                            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No Articles Found</h3>
                            <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                                {searchQuery || activeCategory !== 'All' 
                                    ? `No posts matched "${searchQuery || activeCategory}". Try clearing your filter.`
                                    : "We haven't published any articles yet. Check back soon for exciting updates!"}
                            </p>
                            {(searchQuery || activeCategory !== 'All') && (
                                <button
                                    onClick={() => {
                                        setActiveCategory('All');
                                        setSearchQuery('');
                                    }}
                                    className="px-5 py-2 rounded-full bg-[#bebd19] text-gray-900 font-bold text-sm hover:bg-[#a8a716] transition-colors"
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </Section>

            {/* Pagination */}
            {totalPages > 1 && (
                <Section className="bg-gray-50 pb-16 pt-0">
                    <div className="flex items-center justify-center gap-2">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-[#bebd19] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-sm font-semibold"
                        >
                            <ChevronLeft size={16} /> Previous
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`w-10 h-10 rounded-lg text-sm font-semibold transition-colors ${
                                    page === currentPage
                                        ? 'bg-[#bebd19] text-gray-900 shadow-sm'
                                        : 'bg-white border border-gray-200 text-gray-600 hover:border-[#bebd19]'
                                }`}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-[#bebd19] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1 text-sm font-semibold"
                        >
                            Next <ChevronRight size={16} />
                        </button>
                    </div>
                </Section>
            )}

            {/* Newsletter Subscription */}
            <Section className="bg-white py-20 border-t border-gray-100">
                <div className="max-w-2xl mx-auto text-center px-4">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Stay Updated</h2>
                    <p className="text-gray-500 mb-8">
                        Get our latest articles, tutorials, and tech insights directly in your inbox.
                    </p>
                    {subscribed ? (
                        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl font-medium">
                            🎉 Thank you for subscribing! Check your inbox soon.
                        </div>
                    ) : (
                        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                                required
                                className="flex-grow px-5 py-3 rounded-full border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#bebd19] transition-colors"
                            />
                            <Button type="submit" className="inline-flex items-center justify-center gap-2 rounded-full">
                                Subscribe <Send size={16} />
                            </Button>
                        </form>
                    )}
                </div>
            </Section>
        </>
    );
};

export default Blog;
